/* src/pages/DashboardFacility.jsx */
import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Typography, Grid, Card, CardContent, Button, Tooltip,
  Snackbar, Paper, Box, Chip, Stack, CircularProgress, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton,
  Switch, FormControlLabel, Menu, MenuItem, ListItemText, Link
} from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { jwtDecode } from 'jwt-decode';
import { initKeycloak, getKeycloakInstance } from '../keycloak';
import { initializePaddle } from '@paddle/paddle-js';
import { Trans, useTranslation } from 'react-i18next';
import { kioskStatus, shortDuration } from '../utils/kioskStatus';
import { facilityToDescribe } from '../utils/transferWelcome';
import QrThemeDialog from './qrTheme/QrThemeDialog';
import OnboardingDialog from './onboarding/OnboardingDialog';

// The « Afficher le QR sur une tablette » sheet, served from public/docs in
// the site's languages; another language gets the English one.
const TABLET_GUIDE_LANGS = ['fr', 'en', 'es', 'pt', 'ja', 'zh'];
const tabletGuideUrl = (language) => {
  const lang = String(language || 'en').split('-')[0];
  return `/docs/afficher-qr-tablette-${TABLET_GUIDE_LANGS.includes(lang) ? lang : 'en'}.pdf`;
};
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://192.168.1.17:8080';

// The free tier is gone: every owner now needs one of these three paid
// plans. Only Standard has a 30-day trial configured on the Paddle side;
// Plus and Premium charge from the first day (trial removed from Plus,
// never added to Premium - see backend/migrations/2026-09-19_add_plus_pricing_plan.sql
// and 2026-09-22_remove_plus_premium_trial.sql).
const PRICING = [
  { id: 2, plan_code: 'STD', plan_name: 'Standard', label: 'Standard', monthly_price: 9.99, max_facilities: 1 },
  { id: 4, plan_code: 'MID', plan_name: 'Plus', label: 'Plus', monthly_price: 19.99, max_facilities: 3 },
  { id: 3, plan_code: 'PRO', plan_name: 'Premium', label: 'Premium', monthly_price: 29.99, max_facilities: 10 },
];

// Sentinel for "no active plan" (DB pricing_plan id 1, the former Free
// tier): kept out of PRICING so it's never offered as a purchasable
// option, but still used as a fallback for owners without a paid plan
// (new signups, cancellations) so they read as "no plan" rather than
// silently inheriting the cheapest paid tier's limits.
const NO_PLAN_META = {
  id: 1, plan_code: 'FREE', plan_name: 'Free', label: 'Free', monthly_price: 0, max_facilities: 0,
};

// The free month a new account gets before choosing a plan: one
// establishment for 30 days from the account's creation. Must match
// backend/utils/freeTrial.ts, which enforces it on creation.
const FREE_TRIAL_DAYS = 30;
const FREE_TRIAL_META = { ...NO_PLAN_META, max_facilities: 1 };

// Was hardcoded to 'sandbox' with no way to go live without a code change,
// unlike the backend (PADDLE_ENV=production in its own .env). Defaults below
// preserve today's exact behavior - going live only needs env vars set on the
// production build, not another deploy of this file. See README "Getting
// started" for the full list.
const PADDLE_ENV = process.env.REACT_APP_PADDLE_ENV || 'sandbox';
const PADDLE_CLIENT_TOKEN = process.env.REACT_APP_PADDLE_CLIENT_TOKEN;

const PADDLE_PRICE_BY_PLAN = {
  STD: process.env.REACT_APP_PADDLE_PRICE_STD || 'pri_01karpp5j025pqhw54fe1tw62r',
  MID: process.env.REACT_APP_PADDLE_PRICE_MID || 'pri_01m2wcywnqxf72y428v8bbqdtq',
  PRO: process.env.REACT_APP_PADDLE_PRICE_PRO || 'pri_01karq90pbyh58e4s2wdv1mv0m',
};

// Black when the rating is climbing, red when it's dropping — deliberately
// not the green/red convention used on the Statistic page, per request.
function trendColor(n) {
  if (n > 0) return '#000000';
  if (n < 0) return 'error.main';
  return 'text.secondary';
}

function trendPrefix(n) {
  return n > 0 ? '+' : '';
}

function ensureInlineTargetId() {
  const canAppend = el => !!el && typeof el.appendChild === 'function';

  let el = document.getElementById('paddle-inline');
  if (canAppend(el)) return 'paddle-inline';

  let fallback = document.getElementById('paddle-inline-fallback');
  if (!fallback) {
    fallback = document.createElement('div');
    fallback.id = 'paddle-inline-fallback';
    fallback.style.cssText = 'width:100%;max-width:860px;margin:24px auto;padding:0;';
    document.body.appendChild(fallback);
  }

  return 'paddle-inline-fallback';
}

// Facility types that can turn on conciergerie mode - keep in sync with
// backend/utils/conciergerie.ts.
const CONCIERGERIE_TYPES = ['gite', 'hotel', 'camping'];

// The three cards of « Mon compte »: subscription, e-mails, data.
const accountCardSx = {
  p: 2.75, borderRadius: 3.5, borderColor: '#E3E8F0', display: 'flex', flexDirection: 'column', gap: 1.5,
};

export default function DashboardFacility() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [facilities, setFacilities] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [ownerReady, setOwnerReady] = useState(false); // the owner row exists (POST /owners answered)
  // Per-facility card stats (note / avis / scans) shown on the dashboard
  // cards, keyed by facility id — { [id]: { note, reviewCount, scans30d } }
  const [facilityStats, setFacilityStats] = useState({});
  const [plan, setPlan] = useState(null);
  const [ownerCreatedAt, setOwnerCreatedAt] = useState(null); // starts the free month
  const [weeklyReport, setWeeklyReport] = useState(null); // owner.weekly_report_enabled; null until loaded
  const [weeklyReportError, setWeeklyReportError] = useState('');
  const [lowRatingAlert, setLowRatingAlert] = useState(null); // owner.low_rating_alert_enabled; null until loaded
  const [lowRatingAlertError, setLowRatingAlertError] = useState('');
  const [token, setToken] = useState('');
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState(''); // token's given_name, for the greeting
  const [showQrWarning, setShowQrWarning] = useState(false);
  // The presentation of QRProofs (onboarding/OnboardingDialog.jsx): opens by
  // itself while owner.onboarding_seen_at is NULL, and on « Découvrir QRProofs ».
  const [onboardingOpen, setOnboardingOpen] = useState(false);

  const [checkoutVisible, setCheckoutVisible] = useState(false);
  const [lastChosenPlan, setLastChosenPlan] = useState(null);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [changePlanOpen, setChangePlanOpen] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);

  const paddleRef = useRef(null);
  const hasOpenedRef = useRef(false);

  const safeEmail = useMemo(() => (email || '').trim().toLowerCase(), [email]);

  // A facility just handed over to this owner by an ambassador still has the
  // prospect's bare data: invite them to complete its description
  // (utils/transferWelcome.js) - "Plus tard" postpones it for this session.
  const [describeLater, setDescribeLater] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('describeLater') || '[]'); } catch { return []; }
  });
  const toDescribe = useMemo(() => facilityToDescribe(facilities, describeLater), [facilities, describeLater]);
  const postponeDescribe = (id) => {
    const next = [...describeLater, id];
    setDescribeLater(next);
    try { sessionStorage.setItem('describeLater', JSON.stringify(next)); } catch { /* private mode */ }
  };
  const openDescription = (f) => {
    sessionStorage.setItem('kc_token', token);
    window.open(`ShowFacility/${f.id}/${safeEmail}`, '_blank');
  };

  const anyPending = facilities?.some(f => f.isAutorized !== 1);

  const getPlanMeta = useCallback((p) => {
    if (!p) return NO_PLAN_META;

    const code = String(p.plan_code || '').toUpperCase();
    const name = String(p.plan_name || '').toLowerCase();
    const id = Number(p.plan_id || p.id || 0);

    return (
      PRICING.find(x => x.plan_code === code) ||
      PRICING.find(x => x.plan_name.toLowerCase() === name) ||
      PRICING.find(x => x.id === id) ||
      NO_PLAN_META
    );
  }, []);

  const planMeta = getPlanMeta(plan);
  const currentPlanCode = planMeta?.plan_code || 'FREE';

  const subscriptionStatus = String(
    plan?.subscription_status || ''
  ).trim().toLowerCase();

  const scheduledAction = String(
    plan?.scheduled_action || ''
  ).trim().toLowerCase();

  const isSubscriptionCanceled = subscriptionStatus === 'canceled';

  const isCancelScheduled =
    scheduledAction === 'cancel' ||
    plan?.cancel_at_period_end === 1 ||
    plan?.cancel_at_period_end === true ||
    subscriptionStatus === 'cancel_scheduled';

  const isFreePlan =
    currentPlanCode === 'FREE' ||
    Number(plan?.plan_id) === 1;

  // Free month: without a paid plan, an account may run one establishment
  // for 30 days from its creation (backend/utils/freeTrial.ts).
  const hasNoPaidPlan = isFreePlan || isSubscriptionCanceled;
  const freeTrialEndsAt = ownerCreatedAt
    ? new Date(new Date(ownerCreatedAt).getTime() + FREE_TRIAL_DAYS * 864e5)
    : null;
  const inFreeTrial = hasNoPaidPlan && freeTrialEndsAt && freeTrialEndsAt > new Date();

  // Single source of truth for "what plan is actually in effect right now".
  // A canceled/expired subscription must fall back to the "no plan" state
  // (max_facilities: 0, etc.) even though the underlying `buying_plan` row
  // may still carry a stale paid plan_code/plan_name - except during the
  // account's free month, which allows one establishment.
  const effectivePlanMeta =
    hasNoPaidPlan
      ? (inFreeTrial ? FREE_TRIAL_META : NO_PLAN_META)
      : planMeta;

  const canCancelSubscription =
    !isFreePlan &&
    !isSubscriptionCanceled &&
    !isCancelScheduled &&
    !cancelLoading;

  useEffect(() => {
    const onError = (msg, src, line, col, err) => console.error('[Global Error]', { msg, src, line, col, err });
    const onRejection = (ev) => console.error('[Unhandled Promise Rejection]', ev?.reason || ev);

    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);

    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRejection);
    };
  }, []);

  const refreshPlan = useCallback(async () => {
    if (!token) return null;

    const r = await fetch(`${API_BASE_URL}/admin/getBuyingPlan`, {
      headers: { Authorization: `Bearer ${token}` },
      cache: 'no-store',
    });

    if (r.ok) {
      const bp = await r.json();
      setPlan(bp);
      return bp;
    }

    const fallback = {
      owner_email: safeEmail,
      plan_id: 1,
      plan_code: 'FREE',
      plan_name: 'Free',
      label: 'Free',
      end_of_plan: new Date(Date.now() + 30 * 864e5).toISOString(),
    };

    setPlan(fallback);
    return fallback;
  }, [token, safeEmail]);

  const refreshFacilities = useCallback(async () => {
    if (!token || !safeEmail) return;

    const r = await fetch(`${API_BASE_URL}/ShowFacilityByEmail/${encodeURIComponent(safeEmail)}`, {
      headers: { Authorization: `Bearer ${token}` },
      cache: 'no-store',
    });

    if (r.status === 404) {
      setNotFound(true);
      setFacilities([]);
      return;
    }

    if (!r.ok) throw new Error(`HTTP ${r.status}`);

    const data = await r.json();
    setFacilities(data ? (Array.isArray(data) ? data : [data]) : []);
  }, [token, safeEmail]);

  // Conciergerie mode (documentations/conciergerie-plan.md): opt-in per
  // gîte / hôtel / camping. The backend also forces the QR to no-app mode.
  const [conciergerieError, setConciergerieError] = useState({});
  // « Apparence »: the QR pages' colour and font (qrTheme/QrThemeDialog.jsx)
  const [themeFacility, setThemeFacility] = useState(null);
  // « ⋯ » of an establishment's row: its other actions { anchor, facility }.
  const [rowMenu, setRowMenu] = useState(null);
  const deleteFacility = async (f) => {
    const ok = window.confirm(t('dashboard.deleteConfirm', { name: f.name, id: f.id }));
    if (!ok) return;

    try {
      const r = await fetch(`${API_BASE_URL}/v2/facilities/${f.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!r.ok) {
        const text = await r.text();
        throw new Error(`HTTP ${r.status}: ${text}`);
      }

      setFacilities(prev => (prev ? prev.filter(x => x.id !== f.id) : []));
    } catch (err) {
      console.error('[delete facility] failed:', err);
      alert(t('dashboard.deleteFailed'));
    }
  };

  const toggleConciergerie = useCallback(async (facilityId, enabled) => {
    setConciergerieError((e) => ({ ...e, [facilityId]: '' }));
    try {
      const r = await fetch(`${API_BASE_URL}/v2/facilities/${facilityId}/conciergerie`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ enabled }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setFacilities((list) => list.map((f) => (f.id === facilityId
        ? { ...f, conciergerie_enabled: enabled ? 1 : 0, qr_app_mode: enabled ? 'no_app' : f.qr_app_mode }
        : f)));
    } catch {
      setConciergerieError((e) => ({ ...e, [facilityId]: t('dashboard.conciergerieError') }));
    }
  }, [token, t]);

  // Note/reviews come from the public per-facility endpoint; scans (last 30
  // days) are derived from the owner's own validation history. Only fetched
  // for usable facilities (approved, or still an ambassador's - see `usable`).
  const refreshFacilityStats = useCallback(async (list) => {
    if (!token) return;

    const authorizedFacilities = (list || []).filter(f => f.isAutorized === 1 || Number(f.usable) === 1);
    if (!authorizedFacilities.length) return;

    const results = await Promise.allSettled(
      authorizedFacilities.map(async (f) => {
        const [noteRes, validationsRes] = await Promise.allSettled([
          fetch(`${API_BASE_URL}/public/note/${f.id}`),
          fetch(`${API_BASE_URL}/GetAllValidationById/${f.id}`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        let note = null;
        let reviewCount = null;
        if (noteRes.status === 'fulfilled' && noteRes.value.ok) {
          const data = await noteRes.value.json();
          note = data.note;
          reviewCount = data.reviewCount;
        }

        let scans30d = null;
        let scansPrev30d = null;
        let reviewsThisWeek = null;
        let ratingTrend = null;
        if (validationsRes.status === 'fulfilled' && validationsRes.value.ok) {
          const rows = await validationsRes.value.json();

          if (Array.isArray(rows)) {
            // A rating counts once verified on site ('OK'); a scan refused
            // for being too far ('OUT_OF_RANGE') is a scan, not a rating.
            const ratedRows = rows.filter(r => r.validation_status == null || r.validation_status === 'OK');
            reviewCount = ratedRows.length;

            const day = 864e5;
            const now = Date.now();

            const cutoff7 = now - 7 * day;
            const cutoff30 = now - 30 * day;
            const cutoff60 = now - 60 * day;

            scans30d = rows.filter(r => new Date(r.created_at).getTime() >= cutoff30).length;
            scansPrev30d = rows.filter(r => {
              const t = new Date(r.created_at).getTime();
              return t >= cutoff60 && t < cutoff30;
            }).length;
            reviewsThisWeek = ratedRows.filter(r => new Date(r.created_at).getTime() >= cutoff7).length;

            const avgRatingOf = (list) => {
              const ratings = list
                .map(r => Number(r.rating))
                .filter(n => Number.isFinite(n));
              return ratings.length
                ? ratings.reduce((s, n) => s + n, 0) / ratings.length
                : null;
            };

            // Trend: this facility's own average rating over the current
            // window vs. the equal-length window right before it (7 vs 7
            // days, 30 vs 30 days). Computed client-side from rows we
            // already have, instead of the geo-based nearby-store endpoint
            // used on the Statistic page — that one 404s (and silently
            // hides the badge) for any facility without usable lat/lon,
            // which real establishments can still hit.
            const trendOver = (windowDays) => {
              const currentStart = now - windowDays * day;
              const previousStart = now - 2 * windowDays * day;

              const currentAvg = avgRatingOf(
                ratedRows.filter(r => new Date(r.created_at).getTime() >= currentStart)
              );
              const previousAvg = avgRatingOf(
                ratedRows.filter(r => {
                  const t = new Date(r.created_at).getTime();
                  return t >= previousStart && t < currentStart;
                })
              );

              return currentAvg != null && previousAvg != null
                ? currentAvg - previousAvg
                : null;
            };

            const week = trendOver(7);
            const month = trendOver(30);

            if (week != null || month != null) {
              ratingTrend = { week, month };
            }
          }
        }

        return { id: f.id, note, reviewCount, scans30d, scansPrev30d, reviewsThisWeek, ratingTrend };
      })
    );

    setFacilityStats(prev => {
      const next = { ...prev };
      results.forEach(r => {
        if (r.status === 'fulfilled') next[r.value.id] = r.value;
      });
      return next;
    });
  }, [token]);

  // Plain-language rollup across every establishment, shown at the top of
  // the dashboard (1b-style "questions" summary) above the per-facility
  // cards (1c-style).
  const summary = useMemo(() => {
    const entries = Object.values(facilityStats);
    if (!entries.length) return null;

    const scans30d = entries.reduce((s, e) => s + (e.scans30d || 0), 0);
    const scansPrev30d = entries.reduce((s, e) => s + (e.scansPrev30d || 0), 0);
    const totalReviews = entries.reduce((s, e) => s + (e.reviewCount || 0), 0);
    const reviewsThisWeek = entries.reduce((s, e) => s + (e.reviewsThisWeek || 0), 0);

    const notes = entries.map(e => e.note).filter(n => n != null);
    const avgNote = notes.length ? notes.reduce((s, n) => s + n, 0) / notes.length : null;

    const perFacilityNotes = (facilities || [])
      .filter(f => facilityStats[f.id]?.note != null)
      .map(f => ({ id: f.id, name: f.name, note: facilityStats[f.id].note }));

    const perFacilityWeekTrend = (facilities || [])
      .filter(f => facilityStats[f.id]?.ratingTrend?.week != null)
      .map(f => ({ id: f.id, name: f.name, week: facilityStats[f.id].ratingTrend.week }));

    return {
      scans30d,
      scansDelta: scans30d - scansPrev30d,
      totalReviews,
      reviewsThisWeek,
      avgNote,
      facilityCount: entries.length,
      perFacilityNotes,
      perFacilityWeekTrend,
    };
  }, [facilityStats, facilities]);

  const handleCancelPlan = useCallback(async () => {
    if (!token || !canCancelSubscription) return;

    try {
      setCancelLoading(true);

      const r = await fetch(`${API_BASE_URL}/cancelPlan`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          owner_email: safeEmail,
          plan_code: currentPlanCode,
        }),
      });

      if (!r.ok) {
        const text = await r.text();
        throw new Error(`HTTP ${r.status}: ${text}`);
      }

      setCancelConfirmOpen(false);

      await refreshPlan();
      await refreshFacilities();

      alert(t('dashboard.planCancelSuccess', 'Plan cancellation requested successfully.'));
    } catch (err) {
      console.error('[cancel plan] failed:', err);
      alert(t('dashboard.planCancelFailed', 'Unable to cancel the plan.'));
    } finally {
      setCancelLoading(false);
    }
  }, [
    token,
    safeEmail,
    currentPlanCode,
    refreshPlan,
    refreshFacilities,
    t,
    canCancelSubscription,
  ]);

  useEffect(() => {
    (async () => {
      const kc = await initKeycloak();
      if (!kc.authenticated) return kc.login();

      const raw = kc.token ?? '';
      const claims = jwtDecode(raw);

      setToken(raw);
      setEmail((claims?.email ?? '').toString());
      setFirstName((claims?.given_name ?? '').toString().trim());
    })().catch(console.error);
  }, []);

  useEffect(() => {
    if (!token) return;

    refreshPlan().catch(err => console.warn('[buyingPlan]', err?.message || err));

    fetch(`${API_BASE_URL}/owners`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => (r.ok ? r.json() : null))
      .then(d => {
        setOwnerCreatedAt(d?.owner?.created_at ?? new Date().toISOString());
        if (d?.owner?.weekly_report_enabled != null) setWeeklyReport(Number(d.owner.weekly_report_enabled) === 1);
        if (d?.owner?.low_rating_alert_enabled != null) setLowRatingAlert(Number(d.owner.low_rating_alert_enabled) === 1);
        if (d?.owner) setOwnerReady(true);
        if (d?.owner && !d.owner.onboarding_seen_at) setOnboardingOpen(true);
      })
      .catch(err => console.warn('[owners] ensure failed:', err.message));
  }, [token, refreshPlan]);

  // The site language the owner uses: their e-mails follow it (backend utils/emailI18n.ts).
  // Sent at load and on every language change, once the owner row exists.
  const siteLanguage = (i18n.resolvedLanguage || i18n.language || '').split('-')[0];
  useEffect(() => {
    if (!token || !ownerReady || !siteLanguage) return;
    fetch(`${API_BASE_URL}/owners/language`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ language: siteLanguage }),
    }).catch(err => console.warn('[owners] language not saved:', err.message));
  }, [token, ownerReady, siteLanguage]);

  const closeOnboarding = () => {
    setOnboardingOpen(false);
    fetch(`${API_BASE_URL}/owners/onboarding-seen`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}` },
    }).catch(err => console.warn('[owners] onboarding not saved:', err.message));
  };

  // Weekly statistics e-mail on/off (the e-mail's unsubscribe link can only turn it off).
  const toggleWeeklyReport = async (enabled) => {
    setWeeklyReportError('');
    setWeeklyReport(enabled);
    try {
      const r = await fetch(`${API_BASE_URL}/owners/weekly-report`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
    } catch (err) {
      setWeeklyReport(!enabled);
      setWeeklyReportError(t('dashboard.weeklyReportError'));
    }
  };

  // GDPR access/portability: everything we hold about this owner (GET /owners/me/export).
  const [exportError, setExportError] = useState('');
  const exportMyData = async () => {
    setExportError('');
    try {
      const r = await fetch(`${API_BASE_URL}/owners/me/export`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const url = URL.createObjectURL(await r.blob());
      const a = document.createElement('a');
      a.href = url; a.download = 'qrproof-mes-donnees.json';
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setExportError(t('dashboard.exportError'));
    }
  };

  // E-mail on a rating of 2 stars or less (backend utils/lowRatingAlert.ts).
  const toggleLowRatingAlert = async (enabled) => {
    setLowRatingAlertError('');
    setLowRatingAlert(enabled);
    try {
      const r = await fetch(`${API_BASE_URL}/owners/low-rating-alert`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
    } catch (err) {
      setLowRatingAlert(!enabled);
      setLowRatingAlertError(t('dashboard.weeklyReportError'));
    }
  };

  useEffect(() => {
    if (!token || !safeEmail) return;

    refreshFacilities().catch(err => {
      console.error('fetch facilities:', err);
      setFacilities([]);
    });
  }, [token, safeEmail, refreshFacilities]);

  useEffect(() => {
    if (!facilities || !facilities.length) return;

    refreshFacilityStats(facilities).catch(err => console.warn('[facility stats]', err));
  }, [facilities, refreshFacilityStats]);

  const showQrUrl = useCallback((id) => {
    const rawUrl = `${API_BASE_URL}/v2/generateQr/${id}/mockedPurchased`;
    const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(rawUrl)}`;
    const userGuideUrl = `${window.location.origin}/UserGuide`;

    const qp = {
      browserTitle: t('dashboard.qrPopup.browserTitle'),
      title: t('dashboard.qrPopup.title'),
      subtitle: t('dashboard.qrPopup.subtitle'),
      guideLink: t('dashboard.qrPopup.guideLink'),
      tabletGuide: t('dashboard.qrPopup.tabletGuide'),
      copy: t('dashboard.qrPopup.copy'),
      copied: t('dashboard.qrPopup.copied'),
    };

    const html = `<!DOCTYPE html><html><head><title>${qp.browserTitle}</title>
      <meta charset="utf-8">
      <style>
        *{box-sizing:border-box}
        body{
          margin:0;font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;
          background:#f4f5f7;padding:32px 20px;
        }
        .card{
          max-width:440px;margin:0 auto;background:#fff;border-radius:16px;
          box-shadow:0 10px 30px rgba(20,30,60,0.08);padding:28px 28px 24px;text-align:center;
        }
        h1{font-size:18px;font-weight:800;margin:0 0 4px;color:#111827}
        .subtitle{font-size:13px;color:#6b7280;margin:0 0 24px}
        .option{margin-bottom:24px}
        .qr-frame{
          display:inline-block;padding:14px;background:#fafafa;border:1px solid #eef0f3;
          border-radius:14px;margin-bottom:14px;
        }
        .qr-frame img{display:block;width:180px;height:180px;border-radius:6px}
        .url-box{
          display:flex;align-items:center;gap:8px;background:#f5f6f8;border:1px solid #e5e7eb;
          border-radius:10px;padding:10px 12px;
        }
        .url-box a{
          flex:1;min-width:0;color:#0b4dff;text-decoration:none;font-size:13px;
          word-break:break-all;text-align:left;
        }
        .url-box a:hover{text-decoration:underline}
        .copy-btn{
          flex-shrink:0;border:1px solid #d7dae0;background:#fff;color:#374151;font-size:12px;
          font-weight:600;padding:6px 10px;border-radius:8px;cursor:pointer;
        }
        .copy-btn:hover{background:#f0f1f3}
        .guide-link{
          display:inline-flex;align-items:center;gap:6px;font-size:13px;font-weight:700;
          color:#0b4dff;text-decoration:none;border:1px solid #cdd8ff;padding:8px 16px;
          border-radius:10px;
        }
        .guide-link:hover{background:#f0f4ff}
      </style></head><body>
      <div class="card">
        <h1>${qp.title}</h1>
        <p class="subtitle">${qp.subtitle}</p>

        <div class="option">
          <div class="qr-frame"><img src="${qrSrc}" alt="QR code" /></div>
          <div class="url-box">
            <a href="${rawUrl}" target="_blank" rel="noopener">${rawUrl}</a>
            <button class="copy-btn" data-url="${rawUrl}" type="button">${qp.copy}</button>
          </div>
        </div>

        <a class="guide-link" href="${userGuideUrl}" target="_blank" rel="noopener">${qp.guideLink}</a>
        <div style="margin-top:10px"><a class="guide-link" href="${window.location.origin}${tabletGuideUrl(i18n.resolvedLanguage || i18n.language)}" target="_blank" rel="noopener">${qp.tabletGuide}</a></div>
      </div>
      <script>
        var copiedLabel = ${JSON.stringify(qp.copied)};
        document.querySelectorAll('.copy-btn').forEach(function (b) {
          b.addEventListener('click', function () {
            navigator.clipboard.writeText(b.dataset.url).then(function () {
              var old = b.textContent;
              b.textContent = copiedLabel;
              setTimeout(function () { b.textContent = old; }, 1500);
            }).catch(function () {});
          });
        });
      </script>
      </body></html>`;

    const win = window.open('', '_blank', 'width=480,height=680');

    if (win) {
      win.document.write(html);
      win.document.close();
    }
  }, [t, i18n]);

  const logout = () => getKeycloakInstance().logout({ redirectUri: window.location.href });

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const paddle = await initializePaddle({
          environment: PADDLE_ENV,
          token: PADDLE_CLIENT_TOKEN,
          eventCallback: async (evt) => {
            const name = evt?.name;
            if (!name) return;

            console.log('[Paddle event]', name);

            if (name === 'checkout.closed') {
              hasOpenedRef.current = false;
              setCheckoutVisible(false);
              try { await refreshPlan(); } catch {}
              try { await refreshFacilities(); } catch {}
            }

            if (name === 'checkout.completed') {
              setCheckoutVisible(false);
              hasOpenedRef.current = false;

              for (let i = 0; i < 6; i++) {
                try {
                  const p = await refreshPlan();
                  const meta = getPlanMeta(p);
                  if (meta?.plan_code !== 'FREE') break;
                } catch {}
                await new Promise(r => setTimeout(r, 800));
              }

              try { await refreshFacilities(); } catch {}
            }
          },
        });

        if (!cancelled) {
          paddleRef.current = paddle;
          console.log('[Paddle] initialized');
        }
      } catch (e) {
        console.error('[Paddle init failed]', e);
        alert('Paddle initialization failed. Check token/environment and network.');
      }
    })();

    return () => { cancelled = true; };
  }, [refreshPlan, refreshFacilities, getPlanMeta]);

  const openPlanCheckout = useCallback((planCode) => {
    const e = safeEmail;

    if (!e) {
      alert('Email not loaded yet. Please wait a second and retry.');
      return;
    }

    if (!paddleRef.current) {
      alert('Paddle not initialized yet');
      return;
    }

    if (!PADDLE_PRICE_BY_PLAN[planCode]) {
      alert(`No Paddle price configured for plan: ${planCode}`);
      return;
    }

    setLastChosenPlan(planCode);
    setCheckoutVisible(true);
    hasOpenedRef.current = false;
  }, [safeEmail]);

  useEffect(() => {
    if (!checkoutVisible || !lastChosenPlan) return;

    const paddle = paddleRef.current;
    const priceId = PADDLE_PRICE_BY_PLAN[lastChosenPlan];
    const targetId = ensureInlineTargetId();

    if (!paddle || !priceId || !targetId || !safeEmail) return;
    if (hasOpenedRef.current) return;

    hasOpenedRef.current = true;

    const checkoutPayload = {
      items: [{ priceId, quantity: 1 }],
      customer: { email: safeEmail },
      customData: { owner_email: safeEmail },
    };

    try {
      const el = document.getElementById(targetId);
      if (el) while (el.firstChild) el.removeChild(el.firstChild);
    } catch {}

    const fallbackToOverlay = (reason) => {
      setCheckoutVisible(false);
      console.warn('[Paddle] Falling back to overlay:', reason);

      try {
        paddle.Checkout.open({
          settings: { displayMode: 'overlay' },
          ...checkoutPayload,
        });
      } catch (err) {
        hasOpenedRef.current = false;
        alert(`Unable to open checkout: ${err?.message || err}`);
      }
    };

    const tryInline = () => {
      paddle.Checkout.open({
        settings: {
          displayMode: 'inline',
          frameTarget: targetId,
          frameInitialHeight: '520',
          frameStyle: 'width:100%; min-width:312px; background:transparent; border:none;'
        },
        ...checkoutPayload,
      });

      setTimeout(() => {
        const el = document.getElementById(targetId);
        const hasIframe = !!el && Array.from(el.childNodes).some(n => n.nodeName === 'IFRAME');
        if (!hasIframe) fallbackToOverlay('No iframe appended');
      }, 1200);
    };

    try {
      tryInline();
    } catch (err) {
      fallbackToOverlay(err?.message || err);
    }
  }, [checkoutVisible, lastChosenPlan, safeEmail]);

  if (!facilities) {
    return (
      <Container maxWidth="lg" sx={{ textAlign: 'center', mt: 8 }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          {t('dashboard.loading')}
        </Typography>
      </Container>
    );
  }

  const total = facilities.length;
  const planLimitReached = effectivePlanMeta?.max_facilities ?? 1;

  const planEndDate = plan?.end_of_plan
    ? new Date(plan.end_of_plan).toLocaleDateString(i18n.language)
    : '-';

  const cancellationDateRaw =
    plan?.scheduled_effective_at ||
    plan?.cancellation_effective_at ||
    plan?.canceled_at ||
    plan?.end_of_plan;

  const cancellationDate = cancellationDateRaw
    ? new Date(cancellationDateRaw).toLocaleDateString(i18n.language)
    : '-';

  const planLabelForInline =
    PRICING.find(p => p.plan_code === lastChosenPlan)?.label
    ?? t('dashboard.planStandard', 'Standard');

  // Tier-only label (e.g. "Business") for the Subscription card's chip,
  // where an adjacent status chip ("Canceled" / "Active" / "No plan")
  // already conveys the state — same guard against stale plan_code/plan_name.
  const displayPlanTierLabel =
    isFreePlan || isSubscriptionCanceled
      ? (inFreeTrial ? t('dashboard.freeTrialStatus') : t('dashboard.freePlanStatus'))
      : planMeta?.label ?? t('dashboard.freePlanStatus');

  // Compact "Offre Pro · 2/10" chip for the masthead, mirroring the 1c
  // mockup — the full expiry date and cancel controls live in the
  // subscription section further down instead of cluttering the header.
  const planQuotaLabel = t(inFreeTrial ? 'dashboard.freeTrialQuotaBadge' : 'dashboard.planQuotaBadge', {
    label: displayPlanTierLabel,
    count: total,
    max: effectivePlanMeta?.max_facilities ?? '-',
  });


  // The establishment the week's rating moved most for, in the synthesis.
  const bestWeek = summary?.perFacilityWeekTrend
    .slice()
    .sort((a, b) => Math.abs(b.week) - Math.abs(a.week))[0];

  const headerLinkSx = {
    color: '#DCE7FA', fontWeight: 600, fontSize: 14, textDecoration: 'none', px: 1.25, py: 1.25, borderRadius: 2,
    '&:hover': { color: '#FFFFFF', bgcolor: 'rgba(255,255,255,0.08)' },
  };

  return (
    <>
    {/* App bar: logo, offer, the dashboard's tools, the account. */}
    <Box component="header" sx={{ bgcolor: '#0B3A82', color: '#FFFFFF' }}>
      <Container maxWidth="lg" sx={{ py: 1.5, display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box component="a" href="/" aria-label="QRProof" sx={{ bgcolor: '#FFFFFF', borderRadius: 2, px: 1.25, py: 0.5, display: 'flex' }}>
            <Box component="img" src="/logo-qrproof.png" alt="QRProof" sx={{ height: 30, display: 'block' }} />
          </Box>
          <Chip label={planQuotaLabel} size="small" sx={{ bgcolor: '#DCE7FA', color: '#0B3A82', fontWeight: 700 }} />
        </Stack>

        <Box component="nav" aria-label={t('dashboard.toolsLabel')} sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5 }}>
          {facilities?.some((f) => Number(f.conciergerie_enabled) === 1) && (
            <Box component="button" type="button" onClick={() => navigate('/alerts')} sx={{ ...headerLinkSx, color: '#FFFFFF', font: 'inherit', fontWeight: 600, fontSize: 14, bgcolor: 'transparent', border: 'none', cursor: 'pointer' }}>
              {t('dashboard.alertsButton')}
            </Box>
          )}
          {/* Printable sheet (public/docs): setting up a tablet at the counter. */}
          <Box component="a" href={tabletGuideUrl(i18n.resolvedLanguage || i18n.language)} target="_blank" rel="noopener" sx={headerLinkSx}>
            {t('dashboard.tabletGuideShort')}
          </Box>
          <Box component="button" type="button" onClick={() => setOnboardingOpen(true)} sx={{ ...headerLinkSx, font: 'inherit', fontWeight: 600, fontSize: 14, bgcolor: 'transparent', border: 'none', cursor: 'pointer' }}>
            {t('dashboard.discoverShort')}
          </Box>
          <Box sx={{ width: '1px', height: 24, bgcolor: '#3B66AD', mx: 1, display: { xs: 'none', md: 'block' } }} />
          <Typography variant="body2" sx={{ color: '#DCE7FA', display: { xs: 'none', md: 'block' } }}>{safeEmail}</Typography>
          <Button
            onClick={logout}
            sx={{
              ml: 1, color: '#FFFFFF', border: '1px solid #8FB2E8', borderRadius: 2.5, textTransform: 'none', fontWeight: 600, px: 1.75,
              '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
            }}
          >
            {t('dashboard.logout')}
          </Button>
        </Box>
      </Container>
    </Box>

    <Container maxWidth="lg" sx={{ pt: 4 }}>
      {hasNoPaidPlan && freeTrialEndsAt && (
        <Alert severity={inFreeTrial ? 'success' : 'warning'} sx={{ mb: 3 }}>
          {inFreeTrial
            ? t('dashboard.freeTrialActive', { date: freeTrialEndsAt.toLocaleDateString(i18n.language) })
            : t('dashboard.freeTrialOver')}
        </Alert>
      )}

      {anyPending && (
        <Alert severity="info" sx={{ mb: 3 }}>
          {t('dashboard.verifyingAlert')}
        </Alert>
      )}

      {/* Greeting and the month in one sentence, then one row per establishment. */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between', gap: 3, mb: 3 }}>
        <Box sx={{ flex: '1 1 520px', minWidth: 0, maxWidth: 760 }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 800, letterSpacing: -0.5, mb: 1.25 }}>
            {firstName ? t('dashboard.hello', { name: firstName }) : t('dashboard.helloNoName')}
          </Typography>
          {summary && (
            <Typography sx={{ fontSize: 19, lineHeight: 1.55, color: '#334155', '& b': { color: '#0F172A' } }}>
              <Trans
                i18nKey="dashboard.synthesisScans"
                count={summary.scans30d}
                values={{ count: summary.scans30d }}
                components={{ b: <b /> }}
              />
              {' '}
              <Trans
                i18nKey="dashboard.synthesisReviews"
                count={summary.totalReviews}
                values={{ count: summary.totalReviews, week: summary.reviewsThisWeek }}
                components={{ b: <b /> }}
              />
              {summary.scansDelta !== 0 && (
                <>
                  {' '}
                  {t(summary.scansDelta > 0 ? 'dashboard.synthesisMore' : 'dashboard.synthesisLess', { count: Math.abs(summary.scansDelta) })}
                </>
              )}
              {bestWeek && bestWeek.week !== 0 && (
                <>
                  {' '}
                  <Trans
                    i18nKey={bestWeek.week > 0 ? 'dashboard.synthesisUp' : 'dashboard.synthesisDown'}
                    values={{ name: bestWeek.name, value: Math.abs(bestWeek.week).toFixed(1) }}
                    components={{ b: <Box component="b" sx={bestWeek.week < 0 ? { color: '#B42318 !important' } : undefined} /> }}
                  />
                </>
              )}
            </Typography>
          )}
        </Box>

        <Tooltip title={total >= planLimitReached ? t('dashboard.planLimit', { count: planLimitReached }) : ''}>
          <span>
            <Button
              variant="contained"
              href={`/v2/form/?email=${encodeURIComponent(safeEmail)}`}
              disabled={total >= planLimitReached}
              sx={{ bgcolor: '#1557B0', textTransform: 'none', fontWeight: 700, fontSize: 15, borderRadius: 2.5, px: 2.25, py: 1.25 }}
            >
              + {t('dashboard.createFacility')}
            </Button>
          </span>
        </Tooltip>
      </Box>

      {/* My facilities: one row per establishment, carrying its figures, its
          two main actions and, under « ⋯ », the others. */}
      <Box component="section" sx={{ mb: 5 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', justifyContent: 'space-between', gap: 1, mb: 2 }}>
          <Typography variant="h5" component="h2" sx={{ fontWeight: 800 }}>
            {t('dashboard.myFacilities')}
          </Typography>
          <Typography variant="body2" sx={{ color: '#475569' }}>
            {t('dashboard.facilitiesQuota', { count: total, max: planLimitReached })}
          </Typography>
        </Box>

        {notFound || facilities.length === 0 ? (
          <Typography variant="body1" sx={{ p: 4, color: 'text.secondary', textAlign: 'center' }}>
            {t('dashboard.noFacilities')}
          </Typography>
        ) : (
          <Stack spacing={2}>
            {facilities.map(f => {
              const locationSaved = f.current_registration_step === 'location_saved';
              const authorized = f.isAutorized === 1;
              // Approved, or still owned by an ambassador (created on site, not
              // transferred yet): kiosk, QR and statistics work for the demo scan.
              const usable = authorized || Number(f.usable) === 1;
              const stats = facilityStats[f.id];
              const week = stats?.ratingTrend?.week;
              const k = f.qr_display_mode === 'device' ? kioskStatus(f.device_last_seen) : null;

              const chips = [];
              if (!authorized) chips.push({ key: 'pending', label: t('dashboard.pending'), color: '#8A4B00', bg: '#FFF4E5' });
              if (authorized && !locationSaved) chips.push({ key: 'incomplete', label: t('dashboard.incomplete'), color: '#9A2A1C', bg: '#FDECEA' });
              if (usable && authorized && locationSaved && !k) chips.push({ key: 'active', label: t('dashboard.stateActive'), color: '#0B5D2A', bg: '#E6F4EA' });
              if (k) {
                chips.push({
                  key: 'kiosk',
                  label: k.state === 'never' ? t('dashboard.kioskNever')
                    : t(k.state === 'online' ? 'dashboard.kioskOnline' : 'dashboard.kioskOffline', { ago: shortDuration(k.minutes ?? 0) }),
                  color: k.state === 'online' ? '#0B5D2A' : k.state === 'offline' ? '#9A2A1C' : '#475569',
                  bg: k.state === 'online' ? '#E6F4EA' : k.state === 'offline' ? '#FDECEA' : '#F1F5F9',
                });
              }
              if (Number(f.conciergerie_enabled) === 1) chips.push({ key: 'conciergerie', label: t('dashboard.conciergerieActive'), color: '#334155', bg: '#F1F5F9' });

              const figures = [
                { key: 'note', label: t('dashboard.colRating'), value: stats?.note != null ? Number(stats.note).toFixed(1) : '—' },
                {
                  key: 'week',
                  label: t('dashboard.colThisWeek'),
                  value: week ? `${trendPrefix(week)}${week.toFixed(1)}` : '—',
                  color: week ? trendColor(week) : undefined,
                },
                { key: 'reviews', label: t('dashboard.colReviews'), value: stats?.reviewCount ?? '—' },
                { key: 'scans', label: t('dashboard.colScans'), value: stats?.scans30d ?? '—' },
              ];

              return (
                <Paper
                  key={f.id}
                  component="article"
                  variant="outlined"
                  sx={{ borderRadius: 3.5, borderColor: '#E3E8F0', px: { xs: 2, sm: 2.75 }, py: 2.5, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: { xs: '16px', sm: '20px 32px' } }}
                >
                  <Box sx={{ flex: '1 1 240px', minWidth: 0 }}>
                    <Typography
                      variant="caption"
                      sx={{ display: 'block', color: '#1557B0', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6 }}
                    >
                      {t('dashboard.openedSince', { date: new Date(f.created_at).toLocaleDateString(i18n.language) })}
                    </Typography>
                    <Typography variant="h6" component="h3" sx={{ fontWeight: 800, my: 0.5 }}>{f.name}</Typography>
                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                      {chips.map(c => (
                        <Typography key={c.key} component="span" sx={{ fontSize: 13, fontWeight: 600, color: c.color, bgcolor: c.bg, px: 1.1, py: 0.4, borderRadius: 999 }}>
                          {c.label}
                        </Typography>
                      ))}
                      {Boolean(f.demo_unrestricted) && (
                        <Tooltip title={t('dashboard.demoModeTooltip')}>
                          <Chip label={t('dashboard.demoModeLabel')} size="small" color="secondary" variant="outlined" />
                        </Tooltip>
                      )}
                    </Stack>
                  </Box>

                  {usable && (
                    <Box
                      component="dl"
                      sx={{
                        m: 0, flex: '1 1 380px', display: 'grid', gap: { xs: 2, sm: 3 },
                        gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(4, minmax(0, 1fr))' },
                      }}
                    >
                      {figures.map(x => (
                        <Box key={x.key}>
                          <Typography component="dt" variant="caption" sx={{ color: '#475569', whiteSpace: 'nowrap' }}>{x.label}</Typography>
                          <Typography component="dd" sx={{ m: 0, mt: 0.5, fontSize: 24, fontWeight: 800, lineHeight: 1.1, color: x.color }}>
                            {x.value}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}

                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center" sx={{ width: { xs: '100%', sm: 'auto' } }}>
                    <Button
                      variant="contained"
                      disabled={!usable || !locationSaved}
                      onClick={() => usable && showQrUrl(f.id)}
                      sx={{
                        bgcolor: '#1557B0', textTransform: 'none', fontWeight: 700, borderRadius: 2.5, whiteSpace: 'nowrap',
                        flex: { xs: '1 1 100%', sm: '0 0 auto' }, minHeight: 44,
                      }}
                    >
                      {t('dashboard.showUrl')}
                    </Button>
                    <Button
                      variant="outlined"
                      disabled={!usable}
                      onClick={() => usable && navigate(`/Statistic/${f.id}`)}
                      sx={{
                        color: '#0F172A', borderColor: '#CBD5E1', textTransform: 'none', fontWeight: 600, borderRadius: 2.5, whiteSpace: 'nowrap',
                        flex: { xs: '1 1 0', sm: '0 0 auto' }, minHeight: 44,
                      }}
                    >
                      {t('dashboard.statsShort')}
                    </Button>
                    <IconButton
                      aria-label={t('dashboard.moreActions', { name: f.name })}
                      aria-haspopup="menu"
                      onClick={(e) => setRowMenu({ anchor: e.currentTarget, facility: f })}
                      sx={{ border: '1px solid #CBD5E1', borderRadius: 2.5, width: 44, height: 44, flex: '0 0 auto' }}
                    >
                      <MoreHorizIcon />
                    </IconButton>
                  </Stack>

                  {conciergerieError[f.id] && (
                    <Typography variant="caption" color="error" sx={{ flexBasis: '100%' }}>
                      {conciergerieError[f.id]}
                    </Typography>
                  )}
                </Paper>
              );
            })}
          </Stack>
        )}
      </Box>

      {/* « ⋯ » of a row: the establishment's other actions. */}
      <Menu
        anchorEl={rowMenu?.anchor}
        open={Boolean(rowMenu)}
        onClose={() => setRowMenu(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {rowMenu && [
          <MenuItem key="description" onClick={() => { openDescription(rowMenu.facility); setRowMenu(null); }}>
            {t('dashboard.descriptionAndEvents')}
          </MenuItem>,
          <MenuItem key="appearance" onClick={() => { setThemeFacility(rowMenu.facility); setRowMenu(null); }}>
            {t('dashboard.appearanceMenu')}
          </MenuItem>,
          CONCIERGERIE_TYPES.includes(rowMenu.facility.facility_type) && (
            <MenuItem
              key="conciergerie"
              onClick={() => {
                const f = rowMenu.facility;
                toggleConciergerie(f.id, Number(f.conciergerie_enabled) !== 1);
                setRowMenu(null);
              }}
            >
              <ListItemText primary={t('dashboard.conciergerie')} secondary={t('dashboard.conciergerieHint')} secondaryTypographyProps={{ sx: { whiteSpace: 'normal', maxWidth: 280 } }} />
              <Switch edge="end" size="small" checked={Number(rowMenu.facility.conciergerie_enabled) === 1} tabIndex={-1} inputProps={{ 'aria-hidden': true }} />
            </MenuItem>
          ),
          <MenuItem
            key="delete"
            onClick={() => { const f = rowMenu.facility; setRowMenu(null); deleteFacility(f); }}
            sx={{ color: '#B42318', borderTop: '1px solid #E3E8F0' }}
          >
            {t('dashboard.deleteMenu')}
          </MenuItem>,
        ]}
      </Menu>

      <QrThemeDialog
        open={Boolean(themeFacility)}
        facility={themeFacility}
        token={token}
        onClose={() => setThemeFacility(null)}
      />

      {/* My account: subscription, e-mails, data - three cards under the
          establishments, so billing doesn't compete with them. */}
      {plan && (
        <Box component="section" id="change-plan-section" sx={{ mb: 6 }}>
          <Typography variant="h5" component="h2" sx={{ fontWeight: 800, mb: 2 }}>
            {t('dashboard.accountTitle')}
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }, gap: 2, alignItems: 'stretch' }}>
            <Paper variant="outlined" sx={accountCardSx}>
              <Typography variant="h6" component="h3" sx={{ fontWeight: 700, fontSize: 17 }}>
                {t('dashboard.subscriptionTitle')}
              </Typography>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                <Chip label={displayPlanTierLabel} color="primary" size="small" />
                {isSubscriptionCanceled && (
                  <Chip label={t('dashboard.subscriptionCanceled')} color="error" variant="outlined" size="small" />
                )}
                {!isSubscriptionCanceled && isCancelScheduled && (
                  <Chip label={t('dashboard.cancelScheduled')} color="warning" variant="outlined" size="small" />
                )}
                {!isSubscriptionCanceled && !isCancelScheduled && !isFreePlan && (
                  <Chip label={t('dashboard.subscriptionActive')} color="success" variant="outlined" size="small" />
                )}
                {isFreePlan && !isSubscriptionCanceled && (
                  <Chip
                    label={inFreeTrial
                      ? t('dashboard.freeTrialUntil', { date: freeTrialEndsAt.toLocaleDateString(i18n.language) })
                      : t('dashboard.freePlanStatus')}
                    color={inFreeTrial ? 'success' : 'default'}
                    variant="outlined"
                    size="small"
                  />
                )}
              </Stack>
              <Typography variant="body2" sx={{ color: '#475569', lineHeight: 1.55 }}>
                {isSubscriptionCanceled
                  ? cancellationDate !== '-'
                    ? t('dashboard.subscriptionCanceledOn', { date: cancellationDate })
                    : t('dashboard.subscriptionCanceledMessage')
                  : isCancelScheduled
                    ? cancellationDate !== '-'
                      ? t('dashboard.planCancelsOn', { date: cancellationDate })
                      : t('dashboard.planCancelsAtPeriodEnd')
                    : isFreePlan
                      ? (inFreeTrial
                        ? t('dashboard.freeTrialMessage', { date: freeTrialEndsAt.toLocaleDateString(i18n.language) })
                        : t('dashboard.freePlanMessage'))
                      : t('dashboard.planActiveUntil', { date: planEndDate })}
              </Typography>
              <Typography variant="body2" sx={{ color: '#475569' }}>
                {t('dashboard.planFacilityLimitLabel')}: {effectivePlanMeta?.max_facilities ?? '-'}
              </Typography>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 'auto', pt: 1 }}>
                <Button
                  variant="contained"
                  onClick={() => setChangePlanOpen(true)}
                  sx={{ bgcolor: '#1557B0', textTransform: 'none', fontWeight: 700, borderRadius: 2.5 }}
                >
                  {t('dashboard.changePlanButton')}
                </Button>
                {!isFreePlan && (
                  <Tooltip
                    title={
                      isSubscriptionCanceled
                        ? t('dashboard.subscriptionAlreadyCanceled')
                        : isCancelScheduled
                          ? t('dashboard.subscriptionCancellationAlreadyScheduled')
                          : ''
                    }
                  >
                    <span>
                      <Button
                        color="error"
                        onClick={() => setCancelConfirmOpen(true)}
                        disabled={!canCancelSubscription}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                      >
                        {isSubscriptionCanceled
                          ? t('dashboard.subscriptionCanceled')
                          : isCancelScheduled
                            ? t('dashboard.cancelScheduled')
                            : t('dashboard.cancelSubscription')}
                      </Button>
                    </span>
                  </Tooltip>
                )}
              </Stack>
            </Paper>

            <Paper variant="outlined" sx={accountCardSx}>
              <Typography variant="h6" component="h3" sx={{ fontWeight: 700, fontSize: 17 }}>
                {t('dashboard.notificationsTitle')}
              </Typography>
              {weeklyReport !== null && (
                <Box>
                  <FormControlLabel
                    sx={{ alignItems: 'flex-start', m: 0 }}
                    control={(
                      <Switch
                        size="small"
                        checked={weeklyReport}
                        onChange={(e) => toggleWeeklyReport(e.target.checked)}
                        sx={{ mt: 0.25 }}
                      />
                    )}
                    label={(
                      <Box sx={{ ml: 1 }}>
                        <Typography sx={{ fontWeight: 600, fontSize: 15 }}>{t('dashboard.weeklyReportShort')}</Typography>
                        <Typography variant="body2" sx={{ color: '#475569' }}>{t('dashboard.weeklyReportHint')}</Typography>
                      </Box>
                    )}
                  />
                  {weeklyReportError && (
                    <Typography variant="caption" color="error" sx={{ display: 'block' }}>{weeklyReportError}</Typography>
                  )}
                </Box>
              )}
              {lowRatingAlert !== null && (
                <Box>
                  <FormControlLabel
                    sx={{ alignItems: 'flex-start', m: 0 }}
                    control={(
                      <Switch
                        size="small"
                        checked={lowRatingAlert}
                        onChange={(e) => toggleLowRatingAlert(e.target.checked)}
                        sx={{ mt: 0.25 }}
                      />
                    )}
                    label={(
                      <Box sx={{ ml: 1 }}>
                        <Typography sx={{ fontWeight: 600, fontSize: 15 }}>{t('dashboard.lowRatingAlertShort')}</Typography>
                        <Typography variant="body2" sx={{ color: '#475569' }}>{t('dashboard.lowRatingAlertHint')}</Typography>
                      </Box>
                    )}
                  />
                  {lowRatingAlertError && (
                    <Typography variant="caption" color="error" sx={{ display: 'block' }}>{lowRatingAlertError}</Typography>
                  )}
                </Box>
              )}
            </Paper>

            <Paper variant="outlined" sx={accountCardSx}>
              <Typography variant="h6" component="h3" sx={{ fontWeight: 700, fontSize: 17 }}>
                {t('dashboard.dataTitle')}
              </Typography>
              <Typography variant="body2" sx={{ color: '#475569', lineHeight: 1.55 }}>
                {t('dashboard.dataHint')}
              </Typography>
              <Button
                variant="outlined"
                onClick={exportMyData}
                sx={{ alignSelf: 'flex-start', color: '#0F172A', borderColor: '#CBD5E1', textTransform: 'none', fontWeight: 600, borderRadius: 2.5 }}
              >
                {t('dashboard.exportMyData')}
              </Button>
              {exportError && (
                <Typography variant="caption" color="error">{exportError}</Typography>
              )}
              <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap" sx={{ mt: 'auto' }}>
                <Link href="/confidentialite" target="_blank" rel="noopener" variant="body2">{t('legalPages.privacy')}</Link>
                <Link href="/conditions-generales" target="_blank" rel="noopener" variant="body2">{t('legalPages.terms')}</Link>
              </Stack>
            </Paper>
          </Box>

          {checkoutVisible && (
            <Paper elevation={2} sx={{ mt: 4, p: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                {t('dashboard.checkoutTitle', { plan: planLabelForInline })}
              </Typography>
            </Paper>
          )}
        </Box>
      )}

      {/* Change plan dialog */}
      <Dialog
        open={changePlanOpen}
        onClose={() => setChangePlanOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('dashboard.changePlanTitle')}</DialogTitle>

        <DialogContent dividers>
          <Grid container spacing={4}>
            {PRICING
              .filter(p => p.plan_code !== effectivePlanMeta?.plan_code)
              .map(p => (
               <Grid item key={p.id} xs={12} sm={6}>
                  <Card
                    sx={{
                      p: 3,
                      minHeight: 220,
                      boxShadow: 3,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between'
                    }}
                  >
                    <CardContent sx={{ p: 0 }}>
                      <Typography variant="h5" gutterBottom>{p.label}</Typography>

                      <Typography variant="body1" color="text.secondary">
                        {t('dashboard.planFacilitiesLabel', {
                          count: p.max_facilities,
                          price: p.monthly_price.toFixed(2)
                        })}
                      </Typography>
                    </CardContent>

                    <Button
                      variant="contained"
                      size="large"
                      sx={{ mt: 3, py: 1.5, fontWeight: 'bold' }}
                      onClick={() => {
                        setChangePlanOpen(false);
                        openPlanCheckout(p.plan_code);
                      }}
                      disabled={!PADDLE_PRICE_BY_PLAN[p.plan_code] || !paddleRef.current || !safeEmail}
                    >
                      {t('dashboard.choosePlan', { label: p.label })}
                    </Button>
                  </Card>
                </Grid>
              ))}
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setChangePlanOpen(false)}>
            {t('dashboard.close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Paddle inline target */}
      <div
        id="paddle-inline"
        style={{
          width: '100%',
          minHeight: checkoutVisible ? 520 : 0,
          display: checkoutVisible ? 'block' : 'none'
        }}
      />

      {/* Just transferred to this owner: complete the description */}
      {/* After the transfer invitation, never on top of it. */}
      <OnboardingDialog open={onboardingOpen && !toDescribe} onClose={closeOnboarding} />

      <Dialog open={Boolean(toDescribe)} onClose={() => postponeDescribe(toDescribe.id)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('dashboard.transferWelcomeTitle', { name: toDescribe?.name ?? '' })}</DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ mb: 1.5 }}>{t('dashboard.transferWelcomeText')}</Typography>
          <Typography variant="body2" color="text.secondary">{t('dashboard.transferWelcomeFields')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => postponeDescribe(toDescribe.id)}>{t('dashboard.transferWelcomeLater')}</Button>
          <Button variant="contained" onClick={() => { openDescription(toDescribe); postponeDescribe(toDescribe.id); }}>
            {t('dashboard.transferWelcomeAction')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel confirmation dialog */}
      <Dialog
        open={cancelConfirmOpen && canCancelSubscription}
        onClose={() => !cancelLoading && setCancelConfirmOpen(false)}
      >
        <DialogTitle>
          {t('dashboard.cancelPlanConfirmTitle')}
        </DialogTitle>

        <DialogContent dividers>
          <Typography>
            {t('dashboard.cancelPlanConfirmText')}
          </Typography>

          <Alert severity="warning" sx={{ mt: 2 }}>
            {t('dashboard.planCancelWarning')}
          </Alert>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setCancelConfirmOpen(false)}
            disabled={!canCancelSubscription}
          >
            {t('dashboard.no')}
          </Button>

          <Button
            color="error"
            variant="contained"
            onClick={handleCancelPlan}
            disabled={cancelLoading}
          >
            {cancelLoading
              ? t('dashboard.cancelling')
              : t('dashboard.yesCancelPlan')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={showQrWarning}
        autoHideDuration={4000}
        onClose={() => setShowQrWarning(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <MuiAlert elevation={6} variant="filled" severity="warning">
          {t('dashboard.qrWarning')}
        </MuiAlert>
      </Snackbar>
    </Container>
    </>
  );
}
